/**
 * TenantAdminPage — read-only-by-default inventory of every tenant
 * the platform knows about, with inline edit for admins.
 *
 * Mounted at ``/coach/admin/tenants``. Backed by the
 * ``cfg:client:scenario_os:v1:tenant_registry`` config_store doc,
 * which the install pipeline seeds from
 * ``core/config/tenants/scenario_os/v1/tenant_registry.yaml``.
 *
 * Auth model (server-side):
 *   - Coaches see their own tenant + tenants with show_in_login=true
 *   - Admins see every tenant
 *   - Edit is allowed when (a) admin, or (b) caller.tenant === client_id
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CoachLayout from './CoachLayout';
import { useAuth } from '../../context/AuthContext';
import { tenantAPI } from '../../services/api';

export default function TenantAdminPage() {
  const { isAdmin, tenantId } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // client_id being edited
  const [draft, setDraft] = useState({});
  const [savingMsg, setSavingMsg] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await tenantAPI.list();
      setTenants(Array.isArray(data?.tenants) ? data.tenants : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const startEdit = (t) => {
    setEditing(t.client_id);
    setDraft({
      display_name: t.display_name || '',
      tier: t.tier || 'internal',
      status: t.status || 'active',
      owner_email: t.owner_email || '',
      default_domain: t.default_domain || '',
      show_in_login: !!t.show_in_login,
      domains: (t.domains || []).join(', '),
      notes: t.notes || '',
    });
  };

  const cancelEdit = () => { setEditing(null); setDraft({}); };

  const saveEdit = async (clientId) => {
    setSavingMsg('Saving…');
    try {
      const payload = {
        display_name: draft.display_name,
        tier: draft.tier,
        status: draft.status,
        owner_email: draft.owner_email || null,
        default_domain: draft.default_domain || null,
        show_in_login: !!draft.show_in_login,
        domains: (draft.domains || '').split(',').map((s) => s.trim()).filter(Boolean),
        notes: draft.notes || null,
      };
      await tenantAPI.update(clientId, payload);
      setSavingMsg('Saved.');
      setEditing(null);
      await reload();
    } catch (e) {
      setSavingMsg(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTimeout(() => setSavingMsg(''), 3500);
    }
  };

  if (loading) {
    return <CoachLayout title="Tenants"><div style={{ padding: 24 }}>Loading…</div></CoachLayout>;
  }

  return (
    <CoachLayout title="Tenants">
      <div style={{ padding: 24, maxWidth: 1200 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0 }}>Tenant registry</h2>
          <span style={{ fontSize: 12, color: 'var(--coach-text-muted)' }}>
            caller_tenant = <strong>{tenantId}</strong> · admin = <strong>{String(isAdmin)}</strong> ·
            scope = TeachIQ (config_store)
          </span>
          {savingMsg && <span style={{ fontSize: 12, color: 'var(--coach-primary)', marginLeft: 'auto' }}>{savingMsg}</span>}
          <Link
            to="/coach/admin/adaptive-diagnostics"
            className="btn btn-outline btn-sm"
            style={{ marginLeft: 'auto', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
          >
            Adaptive Diagnostics →
          </Link>
        </div>

        {error && (
          <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', color: 'var(--coach-danger)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, marginBottom: 12 }}>{error}</div>
        )}

        <p style={{ fontSize: 13, color: 'var(--coach-text-muted)', marginBottom: 18 }}>
          Each tenant is a top-level isolation boundary. Branding is per-tenant — edit it
          from the <Link to="/coach/admin/branding">Branding</Link> page.
          The canonical inventory is in <code>cfg:client:scenario_os:v1:tenant_registry</code>,
          seeded from <code>core/config/tenants/scenario_os/v1/tenant_registry.yaml</code>.
          {' '}View cross-domain adaptive system health on the{' '}
          <Link to="/coach/admin/adaptive-diagnostics">Adaptive Diagnostics</Link> page.
        </p>

        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Tier</th>
                <th>Status</th>
                <th>Owner</th>
                <th>Default domain</th>
                <th>Domains</th>
                <th>Login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => {
                const isEditing = editing === t.client_id;
                if (isEditing) {
                  return (
                    <tr key={t.client_id} style={{ background: 'rgba(245,158,11,0.08)' }}>
                      <td>
                        <div><strong>{t.client_id}</strong></div>
                        <input value={draft.display_name} onChange={(e) => setDraft((d) => ({ ...d, display_name: e.target.value }))} style={inlineInput} />
                      </td>
                      <td>
                        <select value={draft.tier} onChange={(e) => setDraft((d) => ({ ...d, tier: e.target.value }))} style={inlineInput}>
                          <option value="internal">internal</option>
                          <option value="partner_pilot">partner_pilot</option>
                          <option value="partner_production">partner_production</option>
                        </select>
                      </td>
                      <td>
                        <select value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))} style={inlineInput}>
                          <option value="active">active</option>
                          <option value="paused">paused</option>
                          <option value="archived">archived</option>
                        </select>
                      </td>
                      <td><input value={draft.owner_email} onChange={(e) => setDraft((d) => ({ ...d, owner_email: e.target.value }))} style={inlineInput} /></td>
                      <td><input value={draft.default_domain} onChange={(e) => setDraft((d) => ({ ...d, default_domain: e.target.value }))} style={inlineInput} /></td>
                      <td><input value={draft.domains} onChange={(e) => setDraft((d) => ({ ...d, domains: e.target.value }))} style={inlineInput} placeholder="comma,separated" /></td>
                      <td>
                        <input type="checkbox" checked={!!draft.show_in_login} onChange={(e) => setDraft((d) => ({ ...d, show_in_login: e.target.checked }))} />
                      </td>
                      <td>
                        <button className="btn-primary btn-sm" onClick={() => saveEdit(t.client_id)}>Save</button>
                        <button className="btn-ghost btn-sm" onClick={cancelEdit} style={{ marginLeft: 4 }}>Cancel</button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={t.client_id} style={t.is_caller_tenant ? { background: 'rgba(34,197,94,0.07)' } : undefined}>
                    <td>
                      <div><strong>{t.display_name}</strong></div>
                      <code style={{ fontSize: 11, color: 'var(--coach-text-muted)' }}>{t.client_id}</code>
                      {t.is_caller_tenant && <span style={pillStyle('#15803d')}>yours</span>}
                    </td>
                    <td><span style={pillStyle('#1e40af')}>{t.tier}</span></td>
                    <td><span style={pillStyle(t.status === 'active' ? '#047857' : '#92400e')}>{t.status}</span></td>
                    <td>{t.owner_email || '—'}</td>
                    <td>{t.default_domain || '—'}</td>
                    <td>{(t.domains || []).join(', ') || '—'}</td>
                    <td>{t.show_in_login ? '✓' : '—'}</td>
                    <td>
                      {t.editable_by_caller ? (
                        <button className="btn-ghost btn-sm" onClick={() => startEdit(t)}>Edit</button>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--coach-text-muted)' }}>read-only</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {tenants.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--coach-text-muted)' }}>No tenants visible to you.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </CoachLayout>
  );
}

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

// Add cell padding/borders via inline style on each <td>/<th> — keeps
// this file self-contained and doesn't require touching coach.css.
const inlineInput = {
  width: '100%',
  padding: '4px 6px',
  border: '1px solid var(--coach-border)',
  borderRadius: 4,
  fontSize: 12,
  boxSizing: 'border-box',
  background: 'var(--coach-input-bg)',
  color: 'var(--coach-text)',
};

const pillStyle = (color) => ({
  display: 'inline-block',
  padding: '2px 8px',
  marginLeft: 6,
  background: `${color}1A`,
  color,
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
});

// Inject default cell padding once (the inline style approach above
// only handles the input fields; rows need a basic look).  We rely on
// the existing CSS but fall back to a small inline style block here.
const _style = (typeof document !== 'undefined') && (() => {
  if (document.getElementById('tenant-admin-styles')) return;
  const el = document.createElement('style');
  el.id = 'tenant-admin-styles';
  el.textContent = `
    table th, table td { padding: 8px 10px; border-bottom: 1px solid var(--coach-border); vertical-align: top; }
    table th { text-align: left; font-size: 11px; font-weight: 600; color: var(--coach-text-muted); text-transform: uppercase; letter-spacing: 0.04em; }
  `;
  document.head.appendChild(el);
})();
